"use client";

import React from "react";
import { MapPin, ArrowRight, Info, ShieldCheck, PhoneCall } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
interface ServiceArea {
  name: string;
  quadrant: string;
  notes: string;
}
interface RestorationService {
  title: string;
  to: string;
}
const dcAreas: ServiceArea[] = [{
  name: "Northwest",
  quadrant: "NW",
  notes: "Serving residential rowhomes, commercial corridors, and historic properties."
}, {
  name: "Northeast",
  quadrant: "NE",
  notes: "Rapid local crew arrival for townhomes, apartments, and multi-unit buildings."
}, {
  name: "Capitol Hill",
  quadrant: "SE / NE",
  notes: "Specialized care for historic brownstones, masonry structures, and government-adjacent offices."
}, {
  name: "Downtown",
  quadrant: "Central",
  notes: "Equipped for multi-story office complexes, retail centers, and hospitality facilities."
}, {
  name: "Georgetown",
  quadrant: "NW",
  notes: "Experienced handling aging pipe infrastructure, historic foundations, and waterfront proximity."
}, {
  name: "Southeast Metro",
  quadrant: "SE",
  notes: "Comprehensive emergency response coverage across residential neighborhoods and community facilities."
}];
const restorationServices: RestorationService[] = [{
  title: "Water damage & flood restoration",
  to: "/WaterDamageFloodRestorationPage"
}, {
  title: "Mold remediation",
  to: "/MoldRemediationPage"
}, {
  title: "Fire, smoke & odor restoration",
  to: "/FireSmokeOdorRestorationPage"
}, {
  title: "Sewage cleanup",
  to: "/SewageCleanupPage"
}, {
  title: "Biohazard cleanup",
  to: "/BiohazardCleanupPage"
}, {
  title: "Asbestos & lead abatement",
  to: "/AsbestosLeadAbatementPage"
}, {
  title: "Reconstruction & design-build",
  to: "/ReconstructionDesignBuildPage"
}, {
  title: "Commercial & HOA property restoration",
  to: "/CommercialHOAPropertyRestorationPage"
}];
export default function DcAreas() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="dcserviceareas" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12">
          <Badge variant="outline" className="mb-3 font-medium">
            District Coverage
          </Badge>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Washington, DC service areas
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            TriServe dispatches dedicated, mobile local crews throughout the District 24/7.
            We coordinate directly on-site with homeowners, property managers, and insurance carriers.
          </p>
        </div>

        {/* Dispatch Disclaimer Alert */}
        <Alert className="mb-12 border-border bg-muted/50 text-foreground">
          <Info className="size-5 text-primary shrink-0" />
          <div className="ml-2">
            <AlertTitle className="text-sm font-semibold text-foreground">
              Mobile crew dispatch model
            </AlertTitle>
            <AlertDescription className="text-sm text-muted-foreground mt-1">
              TriServe dispatches fully equipped mobile response vehicles directly to properties
              in every listed DC neighborhood. TriServe does not claim or operate a physical walk-in
              branch office in Washington, DC; all dispatches originate from our regional mobile response hubs.
            </AlertDescription>
          </div>
        </Alert>

        {/* Service Areas Grid */}
        <div className="mb-16">
          <div className="flex items-center gap-2 mb-6">
            <MapPin className="size-5 text-primary" />
            <h3 className="text-xl font-semibold text-foreground">
              Neighborhoods and sectors served
            </h3>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {dcAreas.map((area, i) => <motion.div key={area.name} variants={itemVariants} data-index={i}>
                <Card className="h-full border border-border bg-card shadow-xs transition-[border-color,box-shadow] hover:border-primary/40 hover:shadow-md">
                  <CardContent className="p-5 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="font-semibold text-base text-card-foreground">
                          {area.name}
                        </span>
                        <span className="text-xs font-mono font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-sm">
                          {area.quadrant}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {area.notes}
                      </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-border/60 flex items-center gap-1.5 text-xs font-medium text-primary">
                      <ShieldCheck className="size-3.5" />
                      <span>Direct local dispatch</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>
        </div>

        {/* Services Directory List */}
        <div className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-xs">
          <div className="max-w-2xl mb-6">
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Available restoration services in Washington, DC
            </h3>
            <p className="text-sm text-muted-foreground">
              Select a specialized restoration capability below to learn more about our on-site
              procedures, cleanup standards, and direct insurance billing.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {restorationServices.map((service, j) => <div key={service.to} data-index={j}>
                <Link to={service.to} className="group flex items-center justify-between p-3.5 rounded-lg border border-border/80 bg-background text-foreground transition-[border-color,background-color,color] hover:border-primary hover:bg-muted/40 h-full">
                  <span className="text-sm font-medium pr-2 group-hover:text-primary">
                    {service.title}
                  </span>
                  <ArrowRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
                </Link>
              </div>)}
          </div>

          {/* Action Row */}
          <div className="mt-8 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground text-center sm:text-left">
              <PhoneCall className="size-4 text-primary shrink-0" />
              <span>Crews ready 24/7 across all DC quadrants. No automated call center runarounds.</span>
            </div>
            <Button asChild className="w-full sm:w-auto">
              <Link to="/ContactRequestServicePage">Request immediate service</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}